import React, { useState, memo } from 'react';
import styled from 'styled-components';
import { BsList, BsGrid } from 'react-icons/bs';
import PropertyCard from '../PropertyCard';

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ViewControls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ResultCount = styled.span`
  color: #333;
`;

const ViewButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const ViewButton = styled.button`
  background: ${props => props.$active ? '#C4845C' : 'transparent'};
  color: ${props => props.$active ? 'white' : '#666'};
  border: 1px solid ${props => props.$active ? '#C4845C' : '#e5e5e5'};
  padding: 8px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    border-color: #C4845C;
    color: ${props => props.$active ? 'white' : '#C4845C'};
  }
`;

const PropertiesGrid = styled.div`
  display: grid;
  grid-template-columns: ${props => props.$viewMode === 'grid' ? 'repeat(auto-fill, minmax(280px, 1fr))' : '1fr'};
  gap: 20px;
  height: auto;
  min-height: 0;
  align-content: start;
`;

const TopLoader = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 3px;
  width: 100%;
  background: linear-gradient(90deg, #0B4F45, #C4845C, #0B4F45);
  background-size: 200% 100%;
  animation: loading 1s linear infinite;
  z-index: 2;

  @keyframes loading {
    0% { background-position: 0% 0; }
    100% { background-position: -200% 0; }
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 32px;
  padding: 16px;
`;

const PageButton = styled.button`
  padding: 8px 16px;
  border: 1px solid ${props => props.active ? '#0B4F45' : '#e5e5e5'};
  background: ${props => props.active ? '#0B4F45' : 'white'};
  color: ${props => props.active ? 'white' : '#333'};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #0B4F45;
    color: ${props => props.active ? 'white' : '#0B4F45'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    border-color: #e5e5e5;
    color: #999;
  }
`;

const PageInfo = styled.span`
  color: #666;
  font-size: 14px;
  margin: 0 16px;
`;

const PropertyList = ({ 
  properties, 
  currentPage, 
  totalPages, 
  onPageChange, 
  onPropertyClick,
  onPropertyHover,
  onPropertyLeave,
  isFavorited, // pode ser função, array, ou boolean
  onFavorite,
  onUnfavorite,
  loading = false,
  viewMode: viewModeProp,
  onViewModeChange,
}) => {
  const [internalViewMode, setInternalViewMode] = useState('list');
  const isControlled = viewModeProp !== undefined;
  const viewMode = isControlled ? viewModeProp : internalViewMode;
  const handleSetViewMode = (mode) => {
    if (onViewModeChange) onViewModeChange(mode);
    if (!isControlled) setInternalViewMode(mode);
  };
  
  console.log('[PropertyList] Renderizando com:', {
    propertiesLength: properties?.length || 0,
    loading,
    currentPage,
    totalPages,
    properties: properties?.slice(0, 2).map(p => ({ id: p.id, title: p.title }))
  });

  const renderPaginationButtons = () => {
    const buttons = [];
    
    // Botão "Anterior"
    buttons.push(
      <PageButton
        key="prev"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        Anterior
      </PageButton>
    );

    // Informação da página atual
    buttons.push(
      <PageInfo key="info">
        Página {currentPage} de {totalPages}
      </PageInfo>
    );

    // Botão "Próxima"
    buttons.push(
      <PageButton
        key="next"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        Próxima
      </PageButton>
    );

    return buttons;
  };

  return (
    <Container>
      {loading && <TopLoader aria-live="polite" aria-busy="true" />}
      <ViewControls>
        <ResultCount>
          {properties.length > 0
            ? 'Carregando as melhores opções para si.'
            : 'Ops! Nesta região ainda não temos imóveis...mas em breve teremos novidades.'}
        </ResultCount>
        <ViewButtons>
          <ViewButton 
            $active={viewMode === 'list'} 
            onClick={() => handleSetViewMode('list')}
            title="Visualização em lista"
          >
            <BsList size={20} />
          </ViewButton>
          <ViewButton 
            $active={viewMode === 'grid'} 
            onClick={() => handleSetViewMode('grid')}
            title="Visualização em grade"
          >
            <BsGrid size={20} />
          </ViewButton>
        </ViewButtons>
      </ViewControls>

      <PropertiesGrid $viewMode={viewMode}>
        {console.log('[PropertyList] properties.length:', properties?.length)}
        {properties.length > 0 ? (
          properties.map((property) => (
            <PropertyCard
              key={property.id}
              property={property}
              viewMode={viewMode}
              onClick={() => onPropertyClick && onPropertyClick(property)}
              onMouseEnter={() => onPropertyHover && onPropertyHover(property)}
              onMouseLeave={onPropertyLeave}
              isFavorited={typeof isFavorited === 'function' ? isFavorited(property.id) : Array.isArray(isFavorited) ? isFavorited.includes(property.id) : !!isFavorited}
              onFavorite={onFavorite}
              onUnfavorite={onUnfavorite}
            />
          ))
        ) : (
          <div style={{ 
            gridColumn: '1 / -1', 
            textAlign: 'center', 
            padding: '60px 20px',
            color: '#666',
            fontSize: '16px'
          }}>
            {loading ? 'Carregando...' : 'Nenhum imóvel encontrado para os filtros selecionados.'}
          </div>
        )}
      </PropertiesGrid>
      
      {totalPages > 1 && (
        <PaginationContainer>
          {renderPaginationButtons()}
        </PaginationContainer>
      )}
    </Container>
  );
};

export default memo(PropertyList);